import {
  PropsWithChildren,
  Suspense,
  useEffect,
  useRef,
  useState,
  type JSX,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Dashboard from "../containers/Dashboard";
import { isDrawerChainRemainder } from "../containers/DrawerHostRoute";
import { stripGlobalDialogSegment } from "@features/ecommerce/constants/cartRouteSegments";
import { wasOpenedInApp } from "shared-ui/src/hooks/routedOverlays";
import { isEmbeddedMode } from "../utils/embeddedMode";
import LoadingOrError from "../containers/LoadingOrError";

type FeatureComponentProps = PropsWithChildren<{
  showDashboard?: boolean;
  authRequired?: boolean;
  validateDrawerPath?: boolean;
  drawerBasePath?: string;
  allowedOverlayPath?: string | string[];
}>;

export const matchesAllowedOverlayPath = (
  allowedOverlayPath: string | string[] | undefined,
  pathname: string,
): boolean => {
  if (allowedOverlayPath == null) return false;
  const patterns = Array.isArray(allowedOverlayPath)
    ? allowedOverlayPath
    : [allowedOverlayPath];
  // Global cart dialogs (added-to-cart) can stack on top of any allowed overlay.
  const stripped = stripGlobalDialogSegment(pathname);
  return patterns.some(
    (pattern) =>
      matchPath(pattern, pathname) != null ||
      (stripped !== pathname && matchPath(pattern, stripped) != null),
  );
};

const withoutOverlayEntryState = (
  state: unknown,
): Record<string, unknown> | undefined => {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return undefined;
  }
  const hostState: Record<string, unknown> = { ...state };
  delete hostState.routedOverlay;
  return Object.keys(hostState).length > 0 ? hostState : undefined;
};

// Must use button click - browsers block auto-popups
function EmbeddedLoginPrompt({ onLogin }: { onLogin: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: "2rem",
        textAlign: "center",
      }}
    >
      <p style={{ marginBottom: "1rem", color: "#666" }}>
        Please sign in to continue
      </p>
      <button
        onClick={onLogin}
        style={{
          padding: "0.75rem 1.5rem",
          fontSize: "1rem",
          backgroundColor: "#1976d2",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        Sign In
      </button>
    </div>
  );
}

const FeatureComponent = ({
  showDashboard = false,
  authRequired = false,
  validateDrawerPath = false,
  drawerBasePath,
  allowedOverlayPath,
  children,
}: FeatureComponentProps): JSX.Element | null => {
  const { isAuthenticated, loginWithPopup, isLoading } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { "*": remainder, prefix } = useParams();
  const [searchParams] = useSearchParams();
  const baseMatch = drawerBasePath
    ? matchPath({ path: drawerBasePath, end: false }, location.pathname)
    : null;
  const validPath =
    !validateDrawerPath ||
    matchesAllowedOverlayPath(allowedOverlayPath, location.pathname) ||
    ((!drawerBasePath || baseMatch != null) &&
      isDrawerChainRemainder(
        location.pathname,
        remainder,
        prefix,
        baseMatch?.pathnameBase,
      ));
  const requiresAuth = validPath && authRequired;
  const displaysDashboard = validPath && showDashboard;

  const [authenticated, setAuthenticated] = useState(false);
  const [popupInProgress, setPopupInProgress] = useState(false);
  const [needsEmbeddedLogin, setNeedsEmbeddedLogin] = useState(false);

  const isEmbedded =
    searchParams.get("embedded") === "true" && isEmbeddedMode();

  const handleEmbeddedLogin = () => {
    setPopupInProgress(true);
    setNeedsEmbeddedLogin(false);
    loginWithPopup()
      .then(() => {
        setPopupInProgress(false);
        setAuthenticated(true);
      })
      .catch((error) => {
        console.error("Popup login failed:", error);
        setPopupInProgress(false);
        setNeedsEmbeddedLogin(true); // Show button again on failure
      });
  };

  // Global cart dialogs are transient: a deep link or refresh to one has no result to show,
  // and the page that would own it may not mount its hook, so drop the segment here.
  const handledGlobalSegmentKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const strippedPath = stripGlobalDialogSegment(location.pathname);
    if (
      strippedPath === location.pathname.replace(/\/+$/, "") ||
      wasOpenedInApp(location) ||
      handledGlobalSegmentKeyRef.current === location.key
    ) {
      return;
    }
    handledGlobalSegmentKeyRef.current = location.key;
    navigate(
      { pathname: strippedPath || "/", search: location.search },
      { replace: true, state: withoutOverlayEntryState(location.state) },
    );
  }, [location, navigate]);

  useEffect(() => {
    if (isLoading) return;

    if (requiresAuth && !isAuthenticated) {
      if (isEmbedded) {
        setNeedsEmbeddedLogin(true);
      } else {
        const fullPath = location.pathname + location.search;
        navigate("/login", {
          state: {
            from: fullPath,
          },
        });
      }
      return;
    }

    setAuthenticated(true);
  }, [
    requiresAuth,
    isAuthenticated,
    isLoading,
    isEmbedded,
    navigate,
    location.pathname,
    location.search,
  ]);

  const content = (
    <Suspense fallback={<LoadingOrError loading />}>
      {validPath ? children : <div>Invalid URL</div>}
    </Suspense>
  );

  if (requiresAuth && isEmbedded && !isAuthenticated && needsEmbeddedLogin) {
    return <EmbeddedLoginPrompt onLogin={handleEmbeddedLogin} />;
  }

  // A public route can leave authenticated state set when navigating to a protected route.
  if (requiresAuth && (popupInProgress || isLoading || !isAuthenticated)) {
    return null;
  }

  if (authenticated && displaysDashboard) {
    return <Dashboard>{content}</Dashboard>;
  }

  if (authenticated && !displaysDashboard) {
    return content;
  }

  return null;
};

export default FeatureComponent;
